<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultEditing, useDefaultGridOptions, useLookup, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useChips } from '@/composables/chips'
import { useWarehouseMovementConfirm } from '@/composables/warehouse-movement-confirm'
import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import NoStockConfirmDialog from '@/components/shared/NoStockConfirmDialog.vue'

import WarehouseMovementEdit from '@/components/shared/WarehouseMovementEdit.vue'

const i18n = getI18n()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const { gridRef } = useGridRef()
const { getWarehouseMovementTypeChips, getWarehouseMovementStatusChips, getServiceTypeChips } = useChips()
const editItem = ref(null)

const props = defineProps({
  warehouse: { type: Object, default: null },
  service: { type: Object, default: null },
})

const localizedMovementTypes = planningStore.getLocalizedWarehouseMovementTypes()
const localizedMovementStatuses = planningStore.getLocalizedWarehouseMovementStatuses()

const isMovementLocked = (data) =>
  props.warehouse?.archived === true || data?.warehouse_archived === true || data?.destination_warehouse_archived === true

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.warehouseMovementsUrl, {
  customParamsFn: () => {
    const params = { service__type: portalStore.serviceTypeEnum.EQUIPMENT }
    if (props.warehouse) params.warehouse = props.warehouse.id
    if (props.service) params.service = props.service.id
    return params
  },
})

const hideConfirmed = ref(false)

const onHideConfirmedChange = (value) => {
  gridDataSource.filter(value ? ['status', '<>', planningStore.warehouseMovementStatusEnum.CONFIRMED] : null)
  gridRef.value.instance.refresh()
}

const { showConfirm, requestConfirm, onConfirmProceed, onConfirmCancel, noStockDialog } = useWarehouseMovementConfirm(
  () => gridRef.value.instance.refresh(),
)

const onRevert = async (e) => {
  await planningStore.revertWarehouseMovementAsync(e.row.data.id)
  await gridRef.value.instance.refresh()
}

const onCancel = async (e) => {
  await planningStore.upsertWarehouseMovementAsync({ id: e.row.data.id, status: planningStore.warehouseMovementStatusEnum.CANCELLED })
  await gridRef.value.instance.refresh()
}

const columns = [
  {
    type: 'buttons',
    buttons: [
      {
        name: 'edit',
        visible: (e) => e.row?.data?.status === planningStore.warehouseMovementStatusEnum.SCHEDULED && !isMovementLocked(e.row?.data),
        onClick: (e) => { editItem.value = { ...e.row.data } },
      },
      {
        name: 'delete',
        visible: (e) => e.row?.data?.status === planningStore.warehouseMovementStatusEnum.SCHEDULED && !e.row?.data?.is_from_confirmed_allocation && !isMovementLocked(e.row?.data),
      },
      {
        icon: 'check',
        hint: i18n.t('common.confirm'),
        visible: (e) => e.row?.data?.status === planningStore.warehouseMovementStatusEnum.SCHEDULED && !isMovementLocked(e.row?.data),
        onClick: (e) => requestConfirm(e.row.data.id),
      },
      {
        icon: 'mdi mdi-undo-variant',
        hint: i18n.t('common.restore'),
        visible: (e) => (e.row?.data?.status === planningStore.warehouseMovementStatusEnum.CONFIRMED || e.row?.data?.status === planningStore.warehouseMovementStatusEnum.CANCELLED) && !isMovementLocked(e.row?.data),
        onClick: onRevert,
      },
      {
        icon: 'mdi mdi-close-octagon-outline',
        hint: i18n.t('common.cancel'),
        visible: (e) => e.row?.data?.status === planningStore.warehouseMovementStatusEnum.SCHEDULED && !e.row?.data?.is_from_confirmed_allocation && !isMovementLocked(e.row?.data),
        onClick: onCancel,
      },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '8rem',
  },
  dataGridColumn.useAvatarColumn({
    cellTemplate: (element, { data }) => {
      const svc = planningStore.getServices().find(s => s.id === data.service)
      const src = svc?.avatar_file || svc?.avatar_url
      if (!src) return
      const img = document.createElement('img')
      img.src = src
      img.style.cssText = 'width:2rem;height:2rem;object-fit:cover;border-radius:50%;display:block;margin:auto'
      element.appendChild(img)
    },
  }),
  {
    dataField: 'service',
    caption: i18n.t('common.service'),
    width: '12rem',
    validationRules: [{ type: 'required' }],
    // Show the service filter visibly in the filter row when scoped to an item.
    filterValue: props.service ? props.service.id : undefined,
    lookup: useLookup({
      dataSource: planningStore.getServices(),
      valueExpr: 'id',
      displayExpr: (item) => planningStore.getServiceName(item),
    }),
  },
  {
    dataField: 'service_serial_number',
    caption: i18n.t('common.serialNumber'),
    width: '10rem',
    allowEditing: false,
  },
  {
    dataField: 'service_internal_serial',
    caption: i18n.t('common.internalSerial'),
    width: '10rem',
    allowEditing: false,
    visible: false,
  },
  dataGridColumn.useServiceTypeColumn(),
  {
    dataField: 'movement_type',
    caption: i18n.t('common.type'),
    width: '8rem',
    validationRules: [{ type: 'required' }],
    lookup: useLookup({ dataSource: localizedMovementTypes, valueExpr: 'id', displayExpr: 'name' }),
    cellTemplate: 'movement-type-cell-template',
  },
  {
    dataField: 'status',
    caption: i18n.t('common.status'),
    width: '8rem',
    allowEditing: false,
    lookup: useLookup({ dataSource: localizedMovementStatuses, valueExpr: 'id', displayExpr: 'name' }),
    cellTemplate: 'status-cell-template',
  },
  {
    dataField: 'quantity',
    caption: i18n.t('common.quantity'),
    dataType: 'number',
    width: '7rem',
    validationRules: [{ type: 'required' }],
  },
  {
    dataField: 'scheduled_at',
    caption: i18n.t('common.scheduledAt'),
    dataType: 'datetime',
    width: '10rem',
    sortOrder: 'desc',
    validationRules: [{ type: 'required' }],
  },
  {
    dataField: 'executed_at',
    caption: i18n.t('common.executedAt'),
    dataType: 'datetime',
    width: '10rem',
    allowEditing: false,
  },
  dataGridColumn.useProjectColumn(),
  dataGridColumn.useWarehouseColumn({
    dataField: 'warehouse',
    caption: i18n.t('common.warehouse'),
    width: '10rem',
    allowEditing: false,
    visible: !!props.service,
  }),
  dataGridColumn.useWarehouseColumn(),
  {
    dataField: 'notes',
    caption: i18n.t('common.notes'),
    width: '12rem',
  },
]

const isWarehouseArchived = props.warehouse?.archived === true

const editing = useDefaultEditing()
if (isWarehouseArchived) {
  editing.allowAdding = editing.allowUpdating = editing.allowDeleting = false
}

const gridOptions = useDefaultGridOptions({
  id: 'warehouse-movements-grid',
  columns,
  dataSource: gridDataSource,
  editing,
  isDetailGrid: true,
  addButton: isWarehouseArchived ? undefined : {
    onClick: () => { editItem.value = {} },
  },
  viewToolbarItems: [{ location: 'after', template: 'hide-confirmed-toggle' }],
})
gridOptions.height = '60vh'

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}
</script>

<template>
  <div>
    <q-dialog :model-value="editItem !== null" class="dialog-md" @hide="updateGrid">
      <warehouse-movement-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :warehouse="warehouse"
        :service="service"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <confirm-dialog
      v-model="showConfirm"
      :message="i18n.t('common.confirmMovementSchedule')"
      icon="mdi-alert"
      icon-color="warning"
      @confirm="onConfirmProceed"
      @cancel="onConfirmCancel"
    />
    <no-stock-confirm-dialog v-model="noStockDialog" />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #hide-confirmed-toggle>
        <q-toggle
          v-model="hideConfirmed"
          :label="i18n.t('common.hideConfirmed')"
          color="primary"
          style="align-self: flex-end;"
          @update:model-value="onHideConfirmedChange"
        />
      </template>
      <template #movement-type-cell-template="{ data: templateOptions }">
        <chip-template :values="getWarehouseMovementTypeChips(templateOptions.data.movement_type)" />
      </template>
      <template #status-cell-template="{ data: templateOptions }">
        <chip-template :values="getWarehouseMovementStatusChips(templateOptions.data.status)" />
      </template>
      <template #service-type-cell-template="{ data: templateOptions }">
        <chip-template :values="getServiceTypeChips(templateOptions.data.service_type)" />
      </template>
    </dx-data-grid>
  </div>
</template>
